import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, TextField, InputLabel, Select, MenuItem, FormControl } from '@material-ui/core';
import { useStyles } from '../Interfaces.style';

const SelectWithOther = ({
  label,
  value,
  onChange,
  min = 1,
  max = 5,
  debounceMs = 3000,
  label_explanation,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [typing, setTyping] = useState(false);
  const [tempValue, setTempValue] = useState('');
  const [error, setError] = useState('');
  const timeoutRef = useRef(null);

  const selectValue = useMemo(() => {
    if (typing) return 'other';
    if (typeof value === 'number' && value >= min && value <= max) return value;
    return 'other';
  }, [value, typing, min, max]);

  const otherValue = typing ? tempValue : typeof value === 'number' && value > max ? value : '';

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  // Handle Select change
  const handleSelectChange = (e) => {
    const v = e.target.value;
    setError('');

    if (v === 'other') {
      // Default "other" starting value
      onChange(max + 1);
    } else {
      onChange(Number(v));
    }
  };

  //  typing  field (with debounce)
  const handleOtherChange = (e) => {
    const v = e.target.value;

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    setTyping(true);
    setTempValue(v);
    setError('');

    //type freely (including empty)
    if (v !== '' && !isNaN(v)) {
      onChange(Number(v));
    }

    // Validate
    timeoutRef.current = setTimeout(() => {
      setTyping(false);

      if (v === '') {
        setError('Value is required');
        onChange(max + 1);
        return;
      }

      const num = parseInt(v);

      if (isNaN(num)) {
        setError('Must be a number');
        onChange(max + 1);
        return;
      }

      if (num < min) {
        setError(`Must be at least ${min}`);
        onChange(max + 1);
        return;
      }

      if (num >= min && num <= max) {
        onChange(num);
        setTempValue('');
        return;
      }

      setError('');
      onChange(num);
      setTempValue('');
    }, debounceMs);
  };

  return (
    <>
      <Typography variant='body2' color='textSecondary' className={classes.label_explanation}>
        {label_explanation}
      </Typography>
      <div className={classes.priors_select_container}>
        <FormControl
          error={!!error}
          variant='outlined'
          size='small'
          className={classes.select_with_other_form_control}>
          <InputLabel id={`${label}-label`}>{label}</InputLabel>

          <Select
            labelId={`${label}-label`}
            id={`${label}-select`}
            value={selectValue}
            onChange={handleSelectChange}
            label={label}
            className={
              selectValue === 'other'
                ? classes.select_with_other_select_other
                : classes.select_with_other_select
            }>
            {[...Array(max)].map((_, i) => {
              const v = i + 1;
              return (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              );
            })}
            <MenuItem value='other'>{t('Settings.other')}</MenuItem>
          </Select>

          {selectValue === 'other' && (
            <div className={classes.priors_other_value_container}>
              <TextField
                value={otherValue}
                onChange={handleOtherChange}
                size='small'
                variant='outlined'
                type='number'
                inputProps={{ min }}
                placeholder={t('Settings.other')}
                error={!!error}
                helperText={error}
              />
            </div>
          )}
        </FormControl>
      </div>
    </>
  );
};

export default SelectWithOther;
